import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Col,
  Form,
  Button,
  Row,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import tooltip from "../../../assets/images/Info.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./CreateEditPromoPricing.scss";
import Sidebar from "../commonAdmin/Sidebar";
import Header from "../commonAdmin/Header";
import Footer from "../commonAdmin/Footer";
import Loader from "../../common/Loader";
import delivaApiService from "../../../common/services/apiService";
import rightTick from "../../../assets/images/CheckCircle.svg";
import infoCircle from "../../../assets/images/InfoCircle.svg";
import CaretDown from "../../../assets/images/CaretDown.svg";
import { defaultFetchCities, normalize } from "./cityService";

import {
  getDistanceUnit,
  getLocalStorage,
  getWeightUnit,
  showErrorToast,
  showSuccessToast,
} from "../../../common/helpers/Utils";
import VehicleTypeSelect from "./VehicleTypeSelect";
import SearchableSelect from "./SearchableSelect";
import GeoLocationInput from "./GeoLocationInput";
import { actionFetchCountries } from "../../../common/redux/actions/actionCountry";
import moment from "moment";
import CONSTANTS from "../../../common/helpers/Constants";

const PAGE = 50;

const FIELD_TOOLTIPS = {
  priority:
    "Determines which promotion is selected when multiple promotions apply to the same delivery. (If 2 promos are of the same priority, the one giving the maximum benefit to the sender is applied)",
  max_recommended_price:
    "Sets the maximum recommended price for a delivery to qualify for the promotion. An exceeded amount will not be eligible for promo",
  sender_flat_amount:
    "Sets a fixed promotional amount that the sender will pay.",
  sender_discount_pct:
    "A percentage discount is applied to the recommended price for the sender.",
  sender_discount_amt:
    "Deducts a fixed amount from the recommended price for the sender.",
  sender_cap_amount:
    "Sets the maximum amount the sender can pay under the promotion.",
  sender_floor_amount:
    "Sets the minimum promotional price that the sender has to pay. (E.g., if the promo price makes the sender price 2000 but the floor price is 2100, then 2100 will be charged.)",
  FIXED_RECOMMENDED_PRICE:
    "Calculates the partner payout based on the recommended price, regardless of the promotional price paid by the sender.",
  PARTNER_FLAT:
    "Sets a fixed amount that the partner will receive for the delivery.",
  PERCENT_OF_SENDER:
    "Calculates the partner payout as a percentage of the price paid by the sender.",
  STANDARD:
    "Calculates the partner payout by deducting the standard platform commission from the sender price.",
  total_budget:
    "Sets the promotional budget/subsidy available for the selected duration.",
  duration:
    "Sets how often the promotion budget is allocated or refreshed, such as daily, weekly, or monthly.",
  total_usage_limit:
    "Sets the maximum number of times the promotion can be used during its active period.",
  per_user_limit:
    "Sets the maximum number of times each sender can use the promotion.",
};

/* ── Single Google Place Input (For States) ── */
class GooglePlaceInput extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: props.value || "",
      suggestions: [],
      showDropdown: false,
      loading: false,
    };
    this.wrapperRef = React.createRef();
    this.autocompleteService = null;
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ inputValue: this.props.value || "" });
    }
  }

  handleClickOutside = (event) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({ showDropdown: false });
    }
  };

  initService = () => {
    if (
      !this.autocompleteService &&
      window.google &&
      window.google.maps &&
      window.google.maps.places
    ) {
      this.autocompleteService =
        new window.google.maps.places.AutocompleteService();
    }
  };

  handleInputChange = (e) => {
    const val = e.target.value;
    this.setState({ inputValue: val, showDropdown: true });
    this.props.onChange(val);

    // Require at least 3 characters before querying Google Places API
    if (!val || val.trim().length < 3) {
      this.setState({ suggestions: [], loading: false });
      return;
    }

    this.initService();
    if (!this.autocompleteService) return;

    const { countryCode, searchType, stateName } = this.props;
    let query = val;
    if (stateName && searchType === "city") {
      query = `${val}, ${stateName}`;
    }

    const request = {
      input: query,
      types:
        searchType === "state" ? ["administrative_area_level_1"] : ["(cities)"],
    };

    if (countryCode) {
      request.componentRestrictions = { country: countryCode.toLowerCase() };
    }

    this.setState({ loading: true });
    this.autocompleteService.getPlacePredictions(
      request,
      (predictions, status) => {
        this.setState({ loading: false });
        if (
          window.google &&
          window.google.maps &&
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          // Filter to strictly ensure state-level predictions if searchType === 'state'
          let filtered = predictions;
          if (searchType === "state") {
            filtered = predictions.filter(
              (p) =>
                p.types &&
                p.types.includes("administrative_area_level_1") &&
                !p.types.includes("locality"),
            );
          }
          this.setState({ suggestions: filtered });
        } else {
          this.setState({ suggestions: [] });
        }
      },
    );
  };

  handleSelectSuggestion = (prediction) => {
    const text =
      (prediction.structured_formatting &&
        prediction.structured_formatting.main_text) ||
      prediction.description;

    this.setState({
      inputValue: text,
      suggestions: [],
      showDropdown: false,
    });
    this.props.onChange(text);
  };

  render() {
    const { label, placeholder, disabled, error } = this.props;
    const { inputValue, suggestions, showDropdown } = this.state;

    return (
      <Form.Group
        className="custom-form position-relative"
        ref={this.wrapperRef}
      >
        {label && (
          <Form.Label className="color-head">
            {typeof label === "string" && label.trim().endsWith("*") ? (
              <>
                {label.replace(/\s*\*$/, "")}{" "}
                <span className="text-danger">*</span>
              </>
            ) : (
              label
            )}
          </Form.Label>
        )}
        <Form.Control
          type="text"
          className="py-0"
          value={inputValue}
          onChange={this.handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) this.setState({ showDropdown: true });
          }}
          placeholder={placeholder || label}
          disabled={disabled}
          autoComplete="off"
        />
        {showDropdown && suggestions.length > 0 && (
          <ul
            className="dropdown-menu show w-100 position-absolute"
            style={{
              maxHeight: "220px",
              overflowY: "auto",
              zIndex: 1050,
              top: "100%",
              left: 0,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              background: "#fff",
              borderRadius: "4px",
              padding: "4px 0",
              margin: "2px 0 0 0",
            }}
          >
            {suggestions.map((item) => {
              const mainText =
                (item.structured_formatting &&
                  item.structured_formatting.main_text) ||
                item.description;
              const secondaryText =
                item.structured_formatting &&
                item.structured_formatting.secondary_text;

              return (
                <li
                  key={item.place_id}
                  className="dropdown-item py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => this.handleSelectSuggestion(item)}
                >
                  <div className="font-weight-medium text-dark">{mainText}</div>
                  {secondaryText && (
                    <small className="text-muted">{secondaryText}</small>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {error && <div className="text-danger small mt-1">{error}</div>}
      </Form.Group>
    );
  }
}

/* ── Multi-City Select with Google Places Autocomplete ── */
/* ── Multi-City Select with Checkboxes & Action Buttons ── */
/* ── Multi-City Checkbox Select (Matching Settings.js UI) ── */
/* ── Multi-City Select with Strict State Validation ── */
/* ── Multi-City Checkbox Select with Strict State Dependency ── */

/* ── Optimized Multi-City Checkbox Select ── */
class MultiCitySelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false,
      citySearch: "",
      tempSelectedCities: Array.isArray(props.selected)
        ? [...props.selected]
        : [],
      cityList: [],
      filteredCities: [], // Cached filtered list to avoid re-calculating on every click
      loading: false,
      loadError: "",
      visibleCount: PAGE,
    };
    this.dropdownRef = React.createRef();
    this.requestId = 0;
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    if (this.props.stateName) this.loadCities();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    this.requestId += 1;
  }

  componentDidUpdate(prevProps, prevState) {
    // 1. Sync external selected prop
    if (prevProps.selected !== this.props.selected) {
      this.setState({
        tempSelectedCities: Array.isArray(this.props.selected)
          ? [...this.props.selected]
          : [],
      });
    }

    // 2. State/country changed -> reset and reload
    if (
      prevProps.stateName !== this.props.stateName ||
      prevProps.countryCode !== this.props.countryCode
    ) {
      this.setState({
        dropdownOpen: false,
        citySearch: "",
        cityList: [],
        filteredCities: [],
        tempSelectedCities: [],
        loadError: "",
        visibleCount: PAGE,
      });

      if (this.props.selected && this.props.selected.length > 0) {
        this.props.onChange([]);
      }

      if (this.props.stateName) this.loadCities();
    }

    // 3. Update filtered list ONLY when search term or cityList changes (not on checkbox toggle)
    if (
      prevState.citySearch !== this.state.citySearch ||
      prevState.cityList !== this.state.cityList
    ) {
      this.updateFilteredCities();
    }
  }

  updateFilteredCities = () => {
    const { cityList, citySearch } = this.state;
    const q = normalize(citySearch);
    if (!q) {
      this.setState({ filteredCities: cityList });
    } else {
      this.setState({
        filteredCities: cityList.filter((c) => normalize(c).includes(q)),
      });
    }
  };

  loadCities = () => {
    const { countryCode, stateName } = this.props;
    const fetchCities = this.props.fetchCities || defaultFetchCities;
    const reqId = (this.requestId += 1);

    this.setState({ loading: true, loadError: "" });

    Promise.resolve(fetchCities({ countryCode, stateName }))
      .then((cities) => {
        if (reqId !== this.requestId) return;
        const list = Array.isArray(cities) ? cities : [];
        this.setState({
          cityList: list,
          filteredCities: list,
          loading: false,
          visibleCount: PAGE,
          loadError: list.length ? "" : `No cities available for ${stateName}`,
        });
      })
      .catch(() => {
        if (reqId !== this.requestId) return;
        this.setState({
          cityList: [],
          filteredCities: [],
          loading: false,
          loadError: "Could not load cities. Please try again.",
        });
      });
  };

  handleClickOutside = (e) => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(e.target)
    ) {
      this.handleCancelCities();
    }
  };

  openDropdown = () => {
    if (this.props.disabled || !this.props.stateName) return;
    this.setState({
      dropdownOpen: true,
      tempSelectedCities: Array.isArray(this.props.selected)
        ? [...this.props.selected]
        : [],
      citySearch: "",
      visibleCount: PAGE,
    });
    if (!this.state.cityList.length && !this.state.loading) this.loadCities();
  };

  toggleDropdown = () => {
    if (this.props.disabled || !this.props.stateName) return;
    if (this.state.dropdownOpen) this.handleCancelCities();
    else this.openDropdown();
  };

  handleCitySearchChange = (e) => {
    const val = e.target.value;
    this.setState({ citySearch: val, dropdownOpen: true, visibleCount: PAGE });
    if (this.props.onInputChange) this.props.onInputChange(val);
  };

  // Instant O(1) set-based toggle
  handleToggleTempCity = (cityName) => {
    this.setState((prev) => {
      const set = new Set(prev.tempSelectedCities);
      if (set.has(cityName)) {
        set.delete(cityName);
      } else {
        set.add(cityName);
      }
      return { tempSelectedCities: Array.from(set) };
    });
  };

  handleSelectAllVisible = () => {
    const { filteredCities } = this.state;
    this.setState((prev) => ({
      tempSelectedCities: [
        ...new Set([...prev.tempSelectedCities, ...filteredCities]),
      ],
    }));
  };

  handleClearAllVisible = () => {
    const visibleSet = new Set(this.state.filteredCities);
    this.setState((prev) => ({
      tempSelectedCities: prev.tempSelectedCities.filter(
        (c) => !visibleSet.has(c),
      ),
    }));
  };

  handleShowMore = () =>
    this.setState((prev) => ({ visibleCount: prev.visibleCount + PAGE }));

  handleAddCities = () => {
    this.props.onChange([...new Set(this.state.tempSelectedCities)]);
    this.setState({ dropdownOpen: false, citySearch: "", visibleCount: PAGE });
  };

  handleCancelCities = () => {
    this.setState({
      dropdownOpen: false,
      citySearch: "",
      visibleCount: PAGE,
      tempSelectedCities: Array.isArray(this.props.selected)
        ? [...this.props.selected]
        : [],
    });
  };

  removeSelectedCity = (cityToRemove) => {
    const current = Array.isArray(this.props.selected)
      ? this.props.selected
      : [];
    this.props.onChange(current.filter((c) => c !== cityToRemove));
  };

  renderList() {
    const { stateName } = this.props;
    const {
      loading,
      loadError,
      tempSelectedCities,
      filteredCities,
      visibleCount,
    } = this.state;

    if (loading) {
      return (
        <div className="px-3 py-2 text-muted small">
          Loading cities in {stateName}...
        </div>
      );
    }

    if (loadError) {
      return <div className="px-3 py-2 text-muted small">{loadError}</div>;
    }

    if (filteredCities.length === 0) {
      return (
        <div className="px-3 py-2 text-muted small">
          No cities match your search in {stateName}
        </div>
      );
    }

    const shown = filteredCities.slice(0, visibleCount);
    // Convert array to Set once for O(1) lookup across all visible items
    const selectedSet = new Set(tempSelectedCities);

    return (
      <>
        {shown.map((cityName) => {
          const checked = selectedSet.has(cityName);
          return (
            <label
              key={cityName}
              className="d-flex align-items-center px-3 py-2 border-bottom gap-2 mb-0"
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              <div className="checkboxContainer">
                <input
                  type="checkbox"
                  className="countryCheckbox me-2"
                  checked={checked}
                  onChange={() => this.handleToggleTempCity(cityName)}
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "4px",
                    accentColor: "#005B36",
                    cursor: "pointer",
                  }}
                />
              </div>
              <span
                className="flex-grow-1 ml-2"
                style={{ fontSize: "14px", color: "#333" }}
              >
                {cityName}
              </span>
            </label>
          );
        })}

        {filteredCities.length > shown.length && (
          <div className="px-3 py-2 text-center">
            <Button
              variant="link"
              className="p-0 small"
              style={{ color: "#005B36", textDecoration: "none" }}
              onClick={this.handleShowMore}
            >
              Show {Math.min(PAGE, filteredCities.length - shown.length)} more (
              {shown.length} of {filteredCities.length})
            </Button>
          </div>
        )}
      </>
    );
  }

  render() {
    const { label, disabled, selected = [], stateName, error } = this.props;
    const { dropdownOpen, citySearch, tempSelectedCities, cityList, loading } =
      this.state;

    const isCityDisabled = disabled || !stateName;
    const dynamicPlaceholder = !stateName
      ? "Select State First"
      : loading
        ? "Loading cities..."
        : `Filter ${cityList.length} cities in ${stateName}`;

    return (
      <Form.Group
        className="custom-form position-relative"
        ref={this.dropdownRef}
      >
        {label && (
          <Form.Label className="color-head">
            {typeof label === "string" && label.trim().endsWith("*") ? (
              <>
                {label.replace(/\s*\*$/, "")}{" "}
                <span className="text-danger">*</span>
              </>
            ) : (
              label
            )}
          </Form.Label>
        )}

        <div
          className="search-box px-2 py-1 d-flex align-items-center justify-content-between form-control"
          style={{
            cursor: isCityDisabled ? "not-allowed" : "pointer",
            border: "1px solid #bbbbbb",
            backgroundColor: isCityDisabled ? "#e9ecef" : "#fff",
          }}
          onClick={this.toggleDropdown}
        >
          <div className="d-flex align-items-center flex-grow-1">
            <input
              type="text"
              className="form-control border-0 shadow-none"
              placeholder={dynamicPlaceholder}
              value={dropdownOpen ? citySearch : ""}
              onChange={this.handleCitySearchChange}
              onFocus={this.openDropdown}
              disabled={isCityDisabled}
              style={{
                border: "none",
                minHeight: "38px",
                background: "transparent",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <img
            src={CaretDown}
            alt="CaretDown"
            onClick={(e) => {
              e.stopPropagation();
              this.toggleDropdown();
            }}
            style={{
              paddingLeft: "6px",
              paddingRight: "4px",
              cursor: isCityDisabled ? "not-allowed" : "pointer",
              height: "14px",
              width: "20px",
            }}
          />
        </div>

        {dropdownOpen && !isCityDisabled && (
          <div
            className="shadow border rounded mt-1 country-multi-dropdown"
            style={{
              position: "absolute",
              background: "#fff",
              width: "100%",
              zIndex: 1050,
              top: "100%",
              left: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!loading && cityList.length > 0 && (
              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
                <span className="small text-muted">
                  {tempSelectedCities.length} selected
                </span>
                <span className="small">
                  <button
                    type="button"
                    className="btn btn-link p-0 small"
                    style={{ color: "#005B36", textDecoration: "none" }}
                    onClick={this.handleSelectAllVisible}
                  >
                    Select all
                  </button>
                  <span className="text-muted mx-2">|</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 small text-muted"
                    style={{ textDecoration: "none" }}
                    onClick={this.handleClearAllVisible}
                  >
                    Clear
                  </button>
                </span>
              </div>
            )}

            <div style={{ maxHeight: "220px", overflowY: "auto" }}>
              {this.renderList()}
            </div>

            <div className="d-flex justify-content-between align-items-center p-2 border-top bg-white">
              <Button
                className="btnDefault settingDropDownBtn minw-100 btnOutline"
                style={{ borderRadius: "20px", padding: "4px 16px" }}
                onClick={this.handleCancelCities}
              >
                Close
              </Button>
              <Button
                className="btnDefault btnPrimary settingDropDownBtn minw-100"
                style={{
                  borderRadius: "20px",
                  padding: "4px 20px",
                  backgroundColor: "#005B36",
                  border: "none",
                }}
                onClick={this.handleAddCities}
              >
                Add
              </Button>
            </div>
          </div>
        )}

        {selected.length > 0 && (
          <div className="d-flex flex-wrap mt-2" style={{ gap: "6px" }}>
            {selected.map((city, idx) => (
              <span
                key={`${city}-${idx}`}
                className="badge"
                style={{
                  backgroundColor: "#E6F4EA",
                  color: "#005B36",
                  fontSize: "12px",
                  padding: "6px 10px",
                  borderRadius: "16px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontWeight: 500,
                }}
              >
                {city}
                {!isCityDisabled && (
                  <span
                    style={{
                      cursor: "pointer",
                      fontSize: "14px",
                      lineHeight: "1",
                    }}
                    onClick={() => this.removeSelectedCity(city)}
                  >
                    &times;
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        {error && <div className="text-danger small mt-1">{error}</div>}
      </Form.Group>
    );
  }
}

const EMPTY_FORM = {
  // Step 0
  name: "",
  description: "",
  start_date: null,
  start_time: null,
  end_date: null,
  end_time: null,
  is_active: "",
  is_draft: false,
  currency: "",
  // Step 1
  request_types: "",
  countries: "",
  origin_states: "",
  origin_cities: [], // array — spec: ["Ibadan North", "Ibadan South-West"]
  origin_location: "",
  dest_states: "",
  dest_cities: [], // array — spec: ["Ibadan North"]
  dest_location: "",
  origin_lat: "",
  origin_lng: "",
  origin_radius_km: "",
  dest_lat: "",
  dest_lng: "",
  dest_radius_km: "",
  min_distance_km: "",
  max_distance_km: "",
  max_recommended_price: "",
  min_weight_kg: "",
  max_weight_kg: "",
  max_package_value: "",
  vehicle_types: [],
  delivery_modes: "DOOR_TO_DOOR",
  // Step 2 — Sender Pricing
  sender_pricing_mode: "FLAT",
  sender_flat_amount: "",
  sender_discount_pct: "",
  sender_discount_amt: "",
  sender_cap_amount: "",
  sender_floor_amount: "",
  // Step 2 — Partner Pricing
  partner_pricing_mode: "FIXED_RECOMMENDED_PRICE",
  partner_flat_amount: "",
  partner_pct_of_sender: "",
  // Step 2 — Usage / Display
  total_budget: "",
  duration: "LIFETIME",
  total_usage_limit: "",
  per_user_limit: "",
  priority: "",
  badge_image: null,
  badge_text: "PROMO",
  stackable_with_promo_codes: "yes",
  show_partner_bid_ceiling: "no",
  promo_message: "",
  partner_message: "",
  terms_url: "",
};

const STEPS = ["General Information", "Eligibility Criteria", "Sender Pricing"];

const MAX_NUMERIC_LENGTH = { total_budget: 9 };

const REQUEST_TYPES = [
  { value: "LOCAL", label: "Local" },
  { value: "INTERSTATE", label: "Interstate" },
  { value: "INTERNATIONAL", label: "International", disabled: true },
];

const SENDER_PRICING_MODES = [
  { value: "FLAT", label: "Fixed Price" },
  { value: "DISCOUNT_PERCENT", label: "Percentage Discount" },
  { value: "DISCOUNT_FIXED", label: "Fixed Discount" },
  { value: "CAPPED", label: "Maximum Price Cap" },
];

const PARTNER_PRICING_MODES = [
  { value: "FIXED_RECOMMENDED_PRICE", label: "Fixed Recommended Price" },
  { value: "FLAT", label: "Fixed Amount" },
  { value: "PERCENT_OF_SENDER", label: "Percentage of Sender Price" },
  { value: "STANDARD", label: "Standard Platform Commission" },
];

const PRIORITY_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const value = `${i + 1}`;
  return { value, label: value };
});

class CreateEditPromoPricing extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      isEdit: false,
      isCopied: false,
      editId: null,
      activeStep: 0,
      showPreview: false,
      form: { ...EMPTY_FORM },
      errors: {},
    };
  }

  componentDidMount() {
    if (!getLocalStorage("adminInfo")) {
      this.props.history.push("/login");
      return;
    }
    if (!this.props.countries || this.props.countries.length === 0) {
      this.props.fetchCountries();
    }
    const state = this.props.location && this.props.location.state;
    if (state && state.id) {
      this.setState(
        {
          isEdit: true,
          editId: state.id,
          isCopied: !!state.isCopied,
        },
        () => this.loadExisting(state.id),
      );
    }
  }

  loadExisting(id) {
    this.setState({ loading: true });
    delivaApiService("getPromoPricingById", { id })
      .then((res) => {
        const data = res && res.data && res.data.resourceData;
        if (data) {
          // 1. Normalize cities to array format
          const originCities = Array.isArray(data.origin_cities)
            ? data.origin_cities
            : data.origin_cities
              ? [data.origin_cities]
              : [];

          const destCities = Array.isArray(data.dest_cities)
            ? data.dest_cities
            : data.dest_cities
              ? [data.dest_cities]
              : [];

          // 2. Extract nested usage object fields
          const usageData = data.usage || {};

          this.setState({
            form: {
              ...EMPTY_FORM,
              ...data,
              // Flatten usage object values
              total_budget: usageData.total_budget ?? data.total_budget ?? "",
              duration: usageData.duration ?? data.duration ?? "LIFETIME",
              total_usage_limit:
                usageData.total_usage_limit ?? data.total_usage_limit ?? "",
              per_user_limit:
                usageData.per_user_limit ?? data.per_user_limit ?? "",

              // Normalized Arrays
              origin_cities: originCities,
              dest_cities: destCities,
              vehicle_types: Array.isArray(data.vehicle_types)
                ? data.vehicle_types
                : [],

              // Booleans converted to string for inputs/radios
              is_active: data.is_active != null ? String(data.is_active) : "",
              stackable_with_promo_codes:
                data.stackable_with_promo_codes === true ||
                data.stackable_with_promo_codes === "yes"
                  ? "yes"
                  : "no",
              show_partner_bid_ceiling:
                data.show_partner_bid_ceiling === true ||
                data.show_partner_bid_ceiling === "yes"
                  ? "yes"
                  : "no",
              priority: data.priority != null ? String(data.priority) : "",

              // Dates & Times
              start_date: data.start_date ? new Date(data.start_date) : null,
              end_date: data.end_date ? new Date(data.end_date) : null,
              start_time: data.start_date ? new Date(data.start_date) : null,
              end_time: data.end_date ? new Date(data.end_date) : null,
            },
            loading: false,
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let numericValue = value.replace(/[^0-9]/g, "");
    if (type === "number" && MAX_NUMERIC_LENGTH[name]) {
      numericValue = numericValue.slice(0, MAX_NUMERIC_LENGTH[name]);
    }

    this.setState((prev) => {
      let updatedVehicleTypes = prev.form.vehicle_types;

      // Filter out cycle and motorcycle if switched to Interstate
      if (
        name === "request_types" &&
        value === "INTERSTATE" &&
        Array.isArray(updatedVehicleTypes)
      ) {
        const excluded = ["BICYCLE", "CYCLE", "MOTORCYCLE", "MOTOR_CYCLE"];
        updatedVehicleTypes = updatedVehicleTypes.filter(
          (type) => !excluded.includes(String(type).toUpperCase()),
        );
      }

      return {
        form: {
          ...prev.form,
          [name]:
            type === "checkbox"
              ? checked
              : type === "number"
                ? numericValue
                : value,
          ...(name === "request_types"
            ? { vehicle_types: updatedVehicleTypes }
            : {}),
        },
        errors: { ...prev.errors, [name]: "" },
      };
    });
  };

  handleDateChange = (field, date) => {
    this.setState((prev) => {
      const updatedForm = { ...prev.form, [field]: date };

      // Reset end_date & end_time if end_date becomes earlier than start_date
      if (
        field === "start_date" &&
        updatedForm.end_date &&
        date &&
        moment(updatedForm.end_date).isBefore(moment(date), "day")
      ) {
        updatedForm.end_date = null;
        updatedForm.end_time = null;
      }

      // Check if start and end are on the same day
      const isSameDay =
        updatedForm.start_date &&
        updatedForm.end_date &&
        moment(updatedForm.start_date).isSame(
          moment(updatedForm.end_date),
          "day",
        );

      // Reset end_time if start & end are same day and end_time <= start_time
      if (isSameDay && updatedForm.start_time && updatedForm.end_time) {
        const startMoment = moment(updatedForm.start_time);
        const endMoment = moment(updatedForm.end_time);

        if (
          endMoment.hours() < startMoment.hours() ||
          (endMoment.hours() === startMoment.hours() &&
            endMoment.minutes() <= startMoment.minutes())
        ) {
          updatedForm.end_time = null;
        }
      }

      return {
        form: updatedForm,
        errors: {
          ...prev.errors,
          [field]: "",
          ...(field === "start_date" || field === "start_time"
            ? { start_date: "", start_time: "" }
            : {}),
          ...(field === "end_date" || field === "end_time"
            ? { end_date: "", end_time: "" }
            : {}),
        },
      };
    });
  };

  getCountryCode(countryName) {
    const match = (this.props.countries || []).find(
      (c) =>
        (c.countryName || c.name || "").toLowerCase() ===
        (countryName || "").toLowerCase(),
    );
    return (
      (match &&
        (match.countryCode ||
          match.iso2 ||
          match.code ||
          match.country_code)) ||
      ""
    );
  }

  getCurrencyOptions() {
    const seen = {};
    const options = (this.props.countries || [])
      .filter((country) => country && country.currency)
      .reduce((acc, country) => {
        if (!seen[country.currency]) {
          seen[country.currency] = true;
          acc.push(country);
        }
        return acc;
      }, [])
      .sort((a, b) => (a.currency || "").localeCompare(b.currency || ""))
      .filter(
        (country) => country.currency === "NGN" || country.currency === "USD",
      )
      .map((country) => ({
        value: country.currency,
        label: country.currencySymbol
          ? `${country.currency} (${country.currencySymbol})`
          : country.currency,
      }));

    return options;
  }

  handleCountryChange = (value) => {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        countries: value,
        origin_states: "",
        origin_cities: [],
        origin_location: "",
        dest_states: "",
        dest_cities: [],
        dest_location: "",
      },
      errors: { ...prev.errors, countries: "" },
    }));
  };

  handleOriginStateChange = (value) => {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        origin_states: value,
        origin_cities: [],
        origin_location: "",
        origin_lat: "",
        origin_lng: "",
      },
      errors: { ...prev.errors, origin_states: "", origin_cities: "" },
    }));
  };

  handleDestStateChange = (value) => {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        dest_states: value,
        dest_cities: [],
        dest_location: "",
        dest_lat: "",
        dest_lng: "",
      },
      errors: { ...prev.errors, dest_states: "", dest_cities: "" },
    }));
  };

  setFormValue = (name) => (value) => {
    this.setState((prev) => ({
      form: { ...prev.form, [name]: value },
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  validateStep(step) {
    const { form, isEdit, isCopied } = this.state;
    const errors = {};
    if (step === 0) {
      if (!form.name.trim()) errors.name = "Name is required.";
      if (!form.is_active && form.is_active !== false)
        errors.is_active = "Status is required.";
      if (!form.start_date) errors.start_date = "Start date is required.";
      if (!form.start_time) errors.start_time = "Start time is required.";
      if (!form.end_date) errors.end_date = "End date is required.";
      if (!form.end_time) errors.end_time = "End time is required.";

      // Precise date-time moments
      let startMoment = null;
      let endMoment = null;

      if (form.start_date && form.start_time) {
        const sDate = new Date(form.start_date);
        const sTime = new Date(form.start_time);
        sDate.setHours(
          sTime.getHours(),
          sTime.getMinutes(),
          sTime.getSeconds() || 0,
          0,
        );
        startMoment = moment(sDate);
      }

      if (form.end_date && form.end_time) {
        const eDate = new Date(form.end_date);
        const eTime = new Date(form.end_time);
        eDate.setHours(
          eTime.getHours(),
          eTime.getMinutes(),
          eTime.getSeconds() || 0,
          0,
        );
        endMoment = moment(eDate);
      }

      const now = moment();

      // 1. Prevent past start date & time (not on edit page)
      if (
        (!isEdit || isCopied) &&
        startMoment &&
        startMoment.isBefore(now, "minute")
      ) {
        if (moment(form.start_date).isBefore(now, "day")) {
          errors.start_date = "Start date cannot be in the past.";
        } else {
          errors.start_time = "Start time cannot be in the past.";
        }
      }

      // 2. Prevent past end date & time
      if (endMoment && endMoment.isBefore(now, "minute")) {
        if (moment(form.end_date).isBefore(now, "day")) {
          errors.end_date = "End date cannot be in the past.";
        } else {
          errors.end_time = "End time cannot be in the past.";
        }
      }

      // 3. Prevent end date/time being before or equal to start date/time
      if (startMoment && endMoment && !endMoment.isAfter(startMoment)) {
        if (moment(form.end_date).isBefore(moment(form.start_date), "day")) {
          errors.end_date = "End date must be on or after start date.";
        } else {
          errors.end_time = "End time must be after start time.";
        }
      }

      if (!form.currency) errors.currency = "Currency is required.";
    }
    if (step === 1) {
      if (!form.request_types)
        errors.request_types = "Delivery Type is required.";

      // Geographic validation (Mandatory for both Local & Interstate)
      if (!form.countries) {
        errors.countries = "Country is required.";
      }
      if (!form.origin_states) {
        errors.origin_states =
          form.request_types === "LOCAL"
            ? "State is required."
            : "Origin state is required.";
      }
      if (!form.origin_cities || form.origin_cities.length === 0) {
        errors.origin_cities =
          form.request_types === "LOCAL"
            ? "City is required."
            : "Origin city is required.";
      }

      // Interstate destination requirements
      if (form.request_types === "INTERSTATE") {
        if (!form.dest_states) {
          errors.dest_states = "Destination state is required.";
        }
        if (!form.dest_cities || form.dest_cities.length === 0) {
          errors.dest_cities = "Destination city is required.";
        }
      }

      // Distance validation
      if (form.min_distance_km === "" || form.min_distance_km == null) {
        errors.min_distance_km = "Initial distance is required.";
      }
      if (form.max_distance_km === "" || form.max_distance_km == null) {
        errors.max_distance_km = "Final distance is required.";
      } else if (
        form.min_distance_km !== "" &&
        form.min_distance_km != null &&
        Number(form.max_distance_km) <= Number(form.min_distance_km)
      ) {
        errors.max_distance_km =
          "Final distance must be greater than initial distance.";
      }

      if (
        form.max_recommended_price === "" ||
        form.max_recommended_price == null
      ) {
        errors.max_recommended_price = "Max recommended price is required.";
      }

      // Weight validation
      if (form.min_weight_kg === "" || form.min_weight_kg == null) {
        errors.min_weight_kg = "Min weight is required.";
      }
      if (form.max_weight_kg === "" || form.max_weight_kg == null) {
        errors.max_weight_kg = "Max weight is required.";
      } else if (
        form.min_weight_kg !== "" &&
        form.min_weight_kg != null &&
        Number(form.max_weight_kg) <= Number(form.min_weight_kg)
      ) {
        errors.max_weight_kg = "Max weight must be greater than min weight.";
      }

      // Vehicle Types validation
      if (!form.vehicle_types || form.vehicle_types.length === 0) {
        errors.vehicle_types = "Vehicle type is required.";
      }
    }

    if (step === 2) {
      // 1. Sender Pricing / Pricing Method
      if (!form.sender_pricing_mode) {
        errors.sender_pricing_mode = "Pricing mode is required.";
      } else {
        if (form.sender_pricing_mode === "FLAT") {
          if (
            form.sender_flat_amount === "" ||
            form.sender_flat_amount == null
          ) {
            errors.sender_flat_amount = "Fixed price is required.";
          }
        } else if (form.sender_pricing_mode === "DISCOUNT_PERCENT") {
          if (
            form.sender_discount_pct === "" ||
            form.sender_discount_pct == null
          ) {
            errors.sender_discount_pct = "Percentage discount is required.";
          }
        } else if (form.sender_pricing_mode === "DISCOUNT_FIXED") {
          if (
            form.sender_discount_amt === "" ||
            form.sender_discount_amt == null
          ) {
            errors.sender_discount_amt = "Discount amount is required.";
          }
        } else if (form.sender_pricing_mode === "CAPPED") {
          if (form.sender_cap_amount === "" || form.sender_cap_amount == null) {
            errors.sender_cap_amount = "Price cap amount is required.";
          }
        }

        if (
          form.sender_floor_amount === "" ||
          form.sender_floor_amount == null
        ) {
          errors.sender_floor_amount = "Floor amount is required.";
        }
      }

      // 2. Partner Pricing
      if (!form.partner_pricing_mode) {
        errors.partner_pricing_mode = "Partner pricing mode is required.";
      } else {
        if (form.partner_pricing_mode === "FLAT") {
          if (
            form.partner_flat_amount === "" ||
            form.partner_flat_amount == null
          ) {
            errors.partner_flat_amount = "Fixed amount is required.";
          }
        } else if (form.partner_pricing_mode === "PERCENT_OF_SENDER") {
          if (
            form.partner_pct_of_sender === "" ||
            form.partner_pct_of_sender == null
          ) {
            errors.partner_pct_of_sender =
              "Percentage of sender price is required.";
          }
        }
      }

      // 3. Usage Controls
      if (form.total_budget === "" || form.total_budget == null) {
        errors.total_budget = "Total budget is required.";
      }
      if (!form.duration) {
        errors.duration = "Duration is required.";
      }
      if (form.total_usage_limit === "" || form.total_usage_limit == null) {
        errors.total_usage_limit = "Maximum total uses is required.";
      }
      if (form.per_user_limit === "" || form.per_user_limit == null) {
        errors.per_user_limit = "Maximum uses per sender is required.";
      }
    }

    return errors;
  }

  handleNext = () => {
    const { activeStep } = this.state;
    const errors = this.validateStep(activeStep);
    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }
    this.setState({
      activeStep: activeStep + 1,
      errors: {},
      showPreview: false,
    });
  };

  handleBack = () => {
    this.setState((prev) => ({
      activeStep: prev.activeStep - 1,
      errors: {},
      showPreview: false,
    }));
  };

  buildPayload(isDraft = false) {
    const { form } = this.state;
    const combineDateTime = (dateObj, timeObj) => {
      if (!dateObj) return null;
      if (timeObj) {
        const d = new Date(dateObj);
        d.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0);
        return moment(d).toISOString();
      }
      return moment(dateObj).toISOString();
    };

    const isLocal = form.request_types === "LOCAL";

    const payload = {
      ...form,
      start_date: combineDateTime(form.start_date, form.start_time),
      end_date: combineDateTime(form.end_date, form.end_time),
      is_active: form.is_active === "true" || form.is_active === true,
      is_draft: Boolean(isDraft), // strictly false on Done, true on Save Draft
      stackable_with_promo_codes: form.stackable_with_promo_codes === "yes",
      show_partner_bid_ceiling: form.show_partner_bid_ceiling === "yes",
      origin_cities:
        Array.isArray(form.origin_cities) && form.origin_cities.length > 0
          ? form.origin_cities
          : null,
      dest_cities: isLocal
        ? Array.isArray(form.origin_cities) && form.origin_cities.length > 0
          ? form.origin_cities
          : null
        : Array.isArray(form.dest_cities) && form.dest_cities.length > 0
          ? form.dest_cities
          : null,
      dest_states: isLocal
        ? form.origin_states || null
        : form.dest_states || null,
    };
    delete payload.start_time;
    delete payload.end_time;
    delete payload.badge_image;

    // Numeric fields — empty string → null
    const numericFields = [
      "min_distance_km",
      "max_distance_km",
      "max_recommended_price",
      "max_package_value",
      "min_weight_kg",
      "max_weight_kg",
      "origin_lat",
      "origin_lng",
      "origin_radius_km",
      "dest_lat",
      "dest_lng",
      "dest_radius_km",
      "sender_flat_amount",
      "sender_discount_pct",
      "sender_discount_amt",
      "sender_cap_amount",
      "sender_floor_amount",
      "partner_flat_amount",
      "partner_pct_of_sender",
      "total_budget",
      "total_usage_limit",
      "per_user_limit",
      "priority",
    ];
    numericFields.forEach((f) => {
      payload[f] =
        payload[f] === "" || payload[f] == null ? null : Number(payload[f]);
    });

    // Optional string fields — empty string → null
    const optionalStrings = [
      "description",
      "origin_states",
      "origin_location",
      "dest_states",
      "dest_location",
      "countries",
      "corridors",
      "delivery_modes",
      "promo_message",
      "partner_message",
      "terms_url",
      "badge_text",
      "request_types",
      "duration",
    ];
    optionalStrings.forEach((f) => {
      if (payload[f] === "") payload[f] = null;
    });

    // vehicle_types — keep as array, empty array → null
    if (!payload.vehicle_types || payload.vehicle_types.length === 0) {
      payload.vehicle_types = null;
    }

    return payload;
  }

  handleSaveDraft = () => {
    const { activeStep } = this.state;
    const errors = this.validateStep(activeStep);
    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }

    const payload = this.buildPayload(true);
    payload.is_active = false;

    this.setState({ loading: true });
    delivaApiService("createPromoPricing", payload)
      .then((res) => {
        this.setState({ loading: false });
        if (res && res.data && res.data.status === 200) {
          this.props.history.push("/promoPricingList");
        }
      })
      .catch(() => this.setState({ loading: false }));
  };

  handleSubmit = () => {
    const { isEdit, isCopied, editId, activeStep } = this.state;
    const errors = this.validateStep(activeStep);
    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }

    const payload = this.buildPayload(false);
    this.setState({ loading: true });
    if (isEdit && !isCopied) {
      payload.id = editId;
      delivaApiService("updatePromoPricing", payload)
        .then((res) => {
          this.setState({ loading: false });
          if (res && res.data && res.data.status === 200) {
            showSuccessToast("Promotional Pricing Updated");
            this.props.history.push("/promoPricingList");
          }
        })
        .catch(() => this.setState({ loading: false }));
    } else {
      delete payload.id;
      delivaApiService("createPromoPricing", { ...payload, is_active: false })
        .then((res) => {
          this.setState({ loading: false });
          if (res && res.data && res.data.status === 200) {
            !isCopied && showSuccessToast("Promotional Pricing Created");
            isCopied && showSuccessToast("Promotion Copied Successfully");
            this.props.history.push("/promoPricingList");
          }
        })
        .catch(() => this.setState({ loading: false }));
    }
  };

  togglePreview = () => {
    const errors = this.validateStep(2);
    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }
    this.setState({ showPreview: true, errors: {} });
  };

  hidePreview = () => {
    this.setState({ showPreview: false });
  };

  goToStep = (step) => {
    this.setState({ activeStep: step, showPreview: false, errors: {} });
  };

  renderPreviewPage() {
    const { form } = this.state;
    const renderValue = (label, value, cols = 4) => (
      <Col md={cols} className="mb-3">
        <div className="pp-preview-item">
          <div className="pp-preview-item-label">{label}</div>
          <div className="pp-preview-item-value">
            {value !== null && value !== undefined && value !== ""
              ? value
              : "--"}
          </div>
        </div>
      </Col>
    );

    const formatDate = (date) =>
      date ? moment(date).format("DD MMM YYYY") : "--";
    const formatTime = (date) => (date ? moment(date).format("hh:mm A") : "--");
    const boolText = (value) =>
      value === "false" || value === false
        ? "No"
        : value === "yes" || value === true
          ? "Yes"
          : "--";

    const lookupLabel = (value, options) =>
      options.find((item) => item.value === value)?.label || value || "--";

    const senderPricingSummary = () => {
      switch (form.sender_pricing_mode) {
        case "FLAT":
          return `Fixed Price${
            form.sender_flat_amount != null && form.sender_flat_amount !== ""
              ? ` (₦${form.sender_flat_amount})`
              : ""
          }`;
        case "DISCOUNT_PERCENT":
          return `Percentage Discount${
            form.sender_discount_pct != null && form.sender_discount_pct !== ""
              ? ` (${form.sender_discount_pct}%)`
              : ""
          }`;
        case "DISCOUNT_FIXED":
          return `Fixed Discount Amount${
            form.sender_discount_amt != null && form.sender_discount_amt !== ""
              ? ` (₦${form.sender_discount_amt})`
              : ""
          }`;
        case "CAPPED":
          return `Maximum Price Cap${
            form.sender_cap_amount != null && form.sender_cap_amount !== ""
              ? ` (₦${form.sender_cap_amount})`
              : ""
          }`;
        default:
          return "--";
      }
    };

    const partnerPricingSummary = () => {
      switch (form.partner_pricing_mode) {
        case "FIXED_RECOMMENDED_PRICE":
          return "Fixed Recommended Price";
        case "FLAT":
          return `Fixed Amount${
            form.partner_flat_amount != null && form.partner_flat_amount !== ""
              ? ` (${this.getCurrencySymbol()} ${form.partner_flat_amount})`
              : ""
          }`;
        case "PERCENT_OF_SENDER":
          return `Percentage of Sender Price${
            form.partner_pct_of_sender != null &&
            form.partner_pct_of_sender !== ""
              ? ` (${form.partner_pct_of_sender}%)`
              : ""
          }`;
        case "STANDARD":
          return "Standard Platform Commission";
        default:
          return "--";
      }
    };

    const formatArrayValues = (val) =>
      Array.isArray(val) && val.length > 0
        ? val
            .map((item) => {
              const text = String(item);
              return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
            })
            .join(", ")
            .replaceAll(/_/g, " ")
        : "--";

    return (
      <div className="pp-preview-page">
        <div className="pp-preview-card mb-4">
          <div className="pp-preview-card-header d-flex justify-content-between align-items-center">
            <p className="pp-preview-section-title">General Information</p>
            <button
              type="button"
              className="pp-preview-link"
              onClick={() => this.goToStep(0)}
            >
              <span className="icon-edit-ico mr-1" />
              <span className="text-decoration-underline ml-1">Edit</span>
            </button>
          </div>
          <div className="pp-preview-section">
            <Row>
              {renderValue("Promotion Name", form.name, 4)}
              {renderValue("Description", form.description, 8)}
            </Row>
            <Row>
              {renderValue(
                "Status",
                form.is_active === "false" || form.is_active === false
                  ? "Inactive"
                  : "Active",
                2,
              )}
              {renderValue("Start Date", formatDate(form.start_date), 2)}
              {renderValue("Start Time", formatTime(form.start_time), 2)}
              {renderValue("End Date", formatDate(form.end_date), 2)}
              {renderValue("End Time", formatTime(form.end_time), 2)}
              {renderValue("Currency", form.currency, 2)}
            </Row>
          </div>
        </div>

        <div className="pp-preview-card mb-4">
          <div className="pp-preview-card-header d-flex justify-content-between align-items-center">
            <p className="pp-preview-section-title">Eligibility Criteria</p>
            <button
              type="button"
              className="pp-preview-link"
              onClick={() => this.goToStep(1)}
            >
              <span className="icon-edit-ico mr-1" />
              <span className="text-decoration-underline ml-1">Edit</span>
            </button>
          </div>
          <div className="pp-preview-section">
            {/* Row 1: Delivery & Origin */}
            <Row>
              {renderValue(
                "Delivery Type",
                lookupLabel(form.request_types, REQUEST_TYPES),
                3,
              )}
              {renderValue("Country", form.countries, 3)}
              {renderValue(
                form.request_types === "INTERSTATE" ? "Origin State" : "State",
                form.origin_states,
                3,
              )}
              {renderValue(
                form.request_types === "INTERSTATE" ? "Origin City" : "City",
                formatArrayValues(form.origin_cities),
                3,
              )}
            </Row>

            {/* Row 2: Locations & Destination */}
            <Row>
              {renderValue("Origin Location", form.origin_location, 3)}
              {form.request_types === "INTERSTATE" && (
                <>
                  {renderValue("Destination State", form.dest_states, 3)}
                  {renderValue(
                    "Destination City",
                    formatArrayValues(form.dest_cities),
                    3,
                  )}
                </>
              )}
              {renderValue("Destination Location", form.dest_location, 3)}
            </Row>

            {/* Row 3: Numeric Constraints */}
            <Row>
              {renderValue(
                "Distance Range",
                form.min_distance_km != null || form.max_distance_km != null
                  ? `${form.min_distance_km ?? "--"} / ${
                      form.max_distance_km ?? "--"
                    } ${getDistanceUnit(form.currency, this.props.countries).toLocaleLowerCase()}`
                  : "--",
                3,
              )}
              {renderValue(
                "Max Recommended Price",
                form.max_recommended_price != null
                  ? `${this.getCurrencySymbol()} ${form.max_recommended_price}`
                  : "--",
                3,
              )}
              {renderValue(
                "Weight Range",
                form.min_weight_kg != null || form.max_weight_kg != null
                  ? `${form.min_weight_kg ?? "--"} / ${
                      form.max_weight_kg ?? "--"
                    } ${getWeightUnit(form.currency, this.props.countries).toLocaleLowerCase()}`
                  : "--",
                3,
              )}
              {renderValue(
                "Maximum Package Value",
                form.max_package_value != null
                  ? `${this.getCurrencySymbol()} ${form.max_package_value}`
                  : "--",
                3,
              )}
            </Row>

            {/* Row 4: Mode & Types */}
            <Row>
              {renderValue(
                "Vehicle Types",
                formatArrayValues(form.vehicle_types),
                6,
              )}
              {renderValue(
                "Delivery Mode",
                form.delivery_modes === "DOOR_TO_DOOR"
                  ? "Door to Door"
                  : form.delivery_modes === "PARK_TO_PARK"
                    ? "Park to Park"
                    : form.delivery_modes || "--",
                6,
              )}
            </Row>
          </div>
        </div>

        <div className="pp-preview-card mb-4">
          <div className="pp-preview-card-header d-flex justify-content-between align-items-center">
            <p className="pp-preview-section-title">Sender Pricing</p>
            <button
              type="button"
              className="pp-preview-link"
              onClick={() => this.goToStep(2)}
            >
              <span className="icon-edit-ico mr-1" />
              <span className="text-decoration-underline ml-1">Edit</span>
            </button>
          </div>
          <div className="pp-preview-section">
            <Row>
              {renderValue("Pricing Method", senderPricingSummary(), 3)}
              {renderValue("Partner Pricing", partnerPricingSummary(), 3)}
            </Row>
            <hr />
            <p className="pp-preview-subtitle">Usage Controls</p>
            <Row>
              {renderValue(
                "Total Budget",
                form.total_budget != null
                  ? `${this.getCurrencySymbol()} ${form.total_budget}`
                  : "--",
                3,
              )}
              {renderValue(
                "Duration",
                lookupLabel(form.duration, CONSTANTS.DURATION_OPTIONS),
                3,
              )}
              {renderValue("Maximum Total Uses", form.total_usage_limit, 3)}
              {renderValue("Maximum Uses Per Sender", form.per_user_limit, 3)}
            </Row>
            <hr />
            <p className="pp-preview-subtitle">Display Settings</p>
            <Row>
              {renderValue(
                "Allow Promo Stacking",
                boolText(form.stackable_with_promo_codes),
                3,
              )}
              {renderValue(
                "Show Max Bid",
                boolText(form.show_partner_bid_ceiling),
                3,
              )}
            </Row>
            <Row>
              {renderValue("Promotional Message", form.promo_message, 12)}
              {renderValue("Partner Message", form.partner_message, 12)}
            </Row>
            <Row>
              {renderValue("Terms & Conditions Link", form.terms_url, 12)}
            </Row>
          </div>
        </div>
      </div>
    );
  }

  renderTooltipIcon(text) {
    if (!text) return null;
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip style={{ width: "347px" }}>{text}</Tooltip>}
      >
        <img
          src={tooltip}
          alt="tooltip"
          className="tooltip-icon infoIcon ml-2"
          style={{ cursor: "pointer", verticalAlign: "middle" }}
        />
      </OverlayTrigger>
    );
  }

  renderLabel(label, tooltipText) {
    if (typeof label === "string" && label.trim().endsWith("*")) {
      const text = label.replace(/\s*\*$/, "");
      return (
        <Form.Label className="color-head">
          {text} <span className="text-danger">*</span>
          {this.renderTooltipIcon(tooltipText)}
        </Form.Label>
      );
    }
    return (
      <Form.Label className="color-head">
        {label}
        {this.renderTooltipIcon(tooltipText)}
      </Form.Label>
    );
  }

  getCurrencySymbol() {
    const { form } = this.state;
    const code = form.currency;
    if (!code) return "";
    const match = (this.props.countries || []).find(
      (c) => (c.currency || "").toUpperCase() === (code || "").toUpperCase(),
    );
    const fromCountry = (match && match.currencySymbol) || null;
    if (fromCountry) return fromCountry;
    const FALLBACK = {
      NGN: "₦",
      USD: "$",
      EUR: "€",
      GBP: "£",
      ZAR: "R",
      KES: "KSh",
      GHS: "₵",
    };
    return FALLBACK[(code || "").toUpperCase()] || code || "";
  }

  moneyInp(
    label,
    name,
    placeholder = "",
    tooltipText = null,
    maxLength = null,
  ) {
    const { form, errors } = this.state;
    const symbol = this.getCurrencySymbol();
    const isPercentField =
      name &&
      (name.endsWith("_pct") || name.includes("pct") || label.includes("%"));
    const showSymbol = symbol && !isPercentField;
    const appliedTooltip = tooltipText || FIELD_TOOLTIPS[name];

    return (
      <Form.Group className="custom-form">
        {this.renderLabel(label, appliedTooltip)}
        <div
          className={`input-prefix-wrapper${showSymbol ? "" : " no-prefix"}`}
        >
          {showSymbol && <span className="input-prefix">{symbol}</span>}
          <Form.Control
            type="number"
            name={name}
            className="py-0 hide-number-spinner"
            value={form[name] ?? ""}
            onChange={this.handleChange}
            onWheel={(e) => e.target.blur()}
            maxLength={maxLength}
            onKeyDown={(e) => {
              if (
                maxLength &&
                e.target.value &&
                e.target.value.length >= maxLength &&
                /^[0-9]$/.test(e.key)
              ) {
                e.preventDefault();
              }
            }}
            placeholder={placeholder || label}
          />
        </div>
        {errors[name] && (
          <div className="text-danger small mt-1">{errors[name]}</div>
        )}
      </Form.Group>
    );
  }

  inp(
    label,
    name,
    type = "text",
    placeholder = "",
    maxLength,
    tooltipText = null,
  ) {
    const { form, errors } = this.state;
    const appliedTooltip = tooltipText || FIELD_TOOLTIPS[name];

    return (
      <Form.Group className="custom-form">
        {this.renderLabel(label, appliedTooltip)}
        <Form.Control
          type={type}
          name={name}
          className={`py-0 ${type === "number" ? "hide-number-spinner" : ""}`}
          value={form[name] ?? ""}
          onChange={this.handleChange}
          onWheel={(e) => type === "number" && e.target.blur()}
          placeholder={placeholder || label}
          maxLength={maxLength}
        />
        {errors[name] && (
          <div className="text-danger small mt-1">{errors[name]}</div>
        )}
      </Form.Group>
    );
  }

  sel(
    label,
    name,
    options,
    placeholder = "",
    disabled = false,
    tooltipText = null,
  ) {
    const { form, errors } = this.state;
    const appliedTooltip = tooltipText || FIELD_TOOLTIPS[name];

    return (
      <Form.Group className="custom-form">
        {this.renderLabel(label, appliedTooltip)}
        <Form.Control
          as="select"
          name={name}
          className="py-0"
          value={form[name] ?? ""}
          onChange={this.handleChange}
          disabled={disabled}
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </Form.Control>
        {errors[name] && (
          <div className="text-danger small mt-1">{errors[name]}</div>
        )}
      </Form.Group>
    );
  }

  datePick(label, field, placeholder, minDate, disabled = false) {
    const { form, errors } = this.state;
    return (
      <Form.Group className="custom-form">
        {this.renderLabel(label)}
        <DatePicker
          selected={form[field]}
          onChange={(d) => this.handleDateChange(field, d)}
          dateFormat="dd MMM yyyy"
          placeholderText={placeholder}
          minDate={minDate || null}
          disabled={disabled}
          autoComplete="off"
          className={`form-control py-0 ${disabled ? "form-control-disabled" : ""}`}
        />
        {errors[field] && (
          <div className="text-danger small mt-1">{errors[field]}</div>
        )}
      </Form.Group>
    );
  }

  timePick(label, field, placeholder, minTime, maxTime, disabled = false) {
    const { form, errors } = this.state;
    return (
      <Form.Group className="custom-form promo-pricing-input">
        {this.renderLabel(label)}
        <DatePicker
          selected={form[field]}
          onChange={(d) => this.handleDateChange(field, d)}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={5}
          dateFormat="HH:mm"
          timeFormat="HH:mm"
          minTime={minTime || undefined}
          maxTime={maxTime || undefined}
          placeholderText={placeholder}
          disabled={disabled}
          autoComplete="off"
          onFocus={(e) => e.target.blur()}
          className={`form-control py-0 ${disabled ? "form-control-disabled" : ""}`}
        />
        {errors[field] && (
          <div className="text-danger small mt-1">{errors[field]}</div>
        )}
      </Form.Group>
    );
  }

  radioGroup(name, options) {
    const { form } = this.state;
    return (
      <div className="d-flex flex-wrap" style={{ gap: 16 }}>
        {options.map((o) => {
          const optionTooltipKey =
            name === "partner_pricing_mode" && o.value === "FLAT"
              ? "PARTNER_FLAT"
              : o.value;
          const optionTooltip = FIELD_TOOLTIPS[optionTooltipKey];

          return (
            <div
              key={o.value}
              className="checkboxContainer d-flex align-items-center"
            >
              <input
                type="radio"
                id={`${name}_${o.value}`}
                name={name}
                checked={form[name] === o.value}
                onChange={() =>
                  this.setState((prev) => ({
                    form: { ...prev.form, [name]: o.value },
                  }))
                }
              />
              <label
                htmlFor={`${name}_${o.value}`}
                className="input-label mb-0"
              >
                {o.label}
              </label>
              {this.renderTooltipIcon(optionTooltip)}
            </div>
          );
        })}
      </div>
    );
  }

  handleOriginCitiesChange = (cities) => {
    const isLocal = this.state.form.request_types === "LOCAL";
    this.setState((prev) => ({
      form: {
        ...prev.form,
        origin_cities: cities,
        // Clear origin location if more than 1 city is selected
        origin_location: cities.length > 1 ? "" : prev.form.origin_location,
        // If local and more than 1 city, also clear dest_location
        dest_location:
          isLocal && cities.length > 1 ? "" : prev.form.dest_location,
      },
      errors: { ...prev.errors, origin_cities: "" },
    }));
  };

  handleDestCitiesChange = (cities) => {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        dest_cities: cities,
        // Clear dest location if more than 1 dest city is selected
        dest_location: cities.length > 1 ? "" : prev.form.dest_location,
      },
      errors: { ...prev.errors, dest_cities: "" },
    }));
  };

  /* ── Step 0: General Information ── */
  renderStep0() {
    const { form, isEdit, isCopied } = this.state;
    const today = new Date();

    // Match CreatePromotion logic for Start Time limits
    const isStartToday =
      form.start_date &&
      new Date(form.start_date).toDateString() === today.toDateString();

    const minStartTime = isStartToday
      ? new Date()
      : new Date(new Date().setHours(0, 0, 0, 0));

    // Match CreatePromotion logic for End Time limits
    const isEndToday =
      form.end_date &&
      new Date(form.end_date).toDateString() === today.toDateString();

    const isSameDay =
      form.start_date &&
      form.end_date &&
      new Date(form.start_date).toDateString() ===
        new Date(form.end_date).toDateString();

    let minEndTime;
    if (isEndToday) {
      minEndTime = new Date();
    } else if (isSameDay && form.start_time) {
      minEndTime = form.start_time;
    } else {
      minEndTime = new Date(new Date().setHours(0, 0, 0, 0));
    }

    const maxTime = new Date().setHours(23, 59, 59, 999);

    return (
      <>
        <Row>
          <Col lg={6}>
            {this.inp(
              "Promotion Name *",
              "name",
              "text",
              "Enter promotion name",
              100,
            )}
          </Col>
          <Col lg={6}>
            {this.inp(
              "Description",
              "description",
              "text",
              "Enter description",
              1000,
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            {this.sel(
              "Status *",
              "is_active",
              [
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ],
              "Select Status",
            )}
          </Col>
          <Col lg={4}>
            {this.datePick(
              "Start Date *",
              "start_date",
              "Select date",
              today, // Disables past dates
              isEdit && !isCopied,
            )}
          </Col>
          <Col lg={4}>
            {this.timePick(
              "Start Time *",
              "start_time",
              "Select time",
              minStartTime,
              maxTime,
              isEdit && !isCopied,
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            {this.datePick(
              "End Date *",
              "end_date",
              "Select date",
              form.start_date || today, // Disables dates prior to start_date
            )}
          </Col>
          <Col lg={4}>
            {this.timePick(
              "End Time *",
              "end_time",
              "Select time",
              minEndTime,
              maxTime,
            )}
          </Col>
          <Col lg={4}>
            {this.sel(
              "Currency *",
              "currency",
              this.getCurrencyOptions(),
              "Select Currency",
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            {this.sel(
              "Priority",
              "priority",
              PRIORITY_OPTIONS,
              "Select Priority",
            )}
          </Col>
        </Row>
      </>
    );
  }

  /* ── Step 1: Eligibility Criteria ── */
  renderStep1() {
    const { form } = this.state;
    const countryCode = this.getCountryCode(form.countries);

    const isLocal = form.request_types === "LOCAL";

    const originCitiesCount = Array.isArray(form.origin_cities)
      ? form.origin_cities.length
      : 0;
    const destCitiesCount = Array.isArray(form.dest_cities)
      ? form.dest_cities.length
      : 0;

    // Origin Location is disabled if no country selected OR more than 1 origin city selected
    const isOriginLocationDisabled = !form.countries || originCitiesCount > 1;

    // Destination Location is disabled:
    // - For LOCAL: if no country selected OR more than 1 origin city selected
    // - For INTERSTATE: if no country selected OR more than 1 destination city selected
    const isDestLocationDisabled =
      !form.countries ||
      (isLocal ? originCitiesCount > 1 : destCitiesCount > 1);

    const countryOptions = [...(this.props.countries || [])]
      .filter(Boolean)
      .sort((a, b) =>
        (a.countryName || a.name || "").localeCompare(
          b.countryName || b.name || "",
        ),
      )
      .filter(
        (country) => country.currency === "NGN" || country.currency === "USD",
      )
      .map((item) => ({
        label: item.countryName || item.name || "",
        value: item.countryName || item.name || "",
      }))
      .filter((item) => item.label);

    return (
      <>
        <Row>
          <Col lg={4}>
            {this.sel(
              "Delivery Type *",
              "request_types",
              REQUEST_TYPES,
              "Select Delivery Type",
            )}
          </Col>
          <Col lg={4}>
            <SearchableSelect
              label="Country *"
              placeholder="Select Country"
              options={countryOptions}
              value={form.countries}
              onChange={this.handleCountryChange}
              error={this.state.errors.countries}
            />
          </Col>
          <Col lg={4}>
            <GooglePlaceInput
              label={
                form.request_types === "LOCAL" ? "State *" : "Origin State *"
              }
              placeholder={`Search ${
                form.request_types === "LOCAL" ? "State" : "Origin State"
              }`}
              value={form.origin_states}
              onChange={this.handleOriginStateChange}
              searchType="state"
              countryCode={countryCode}
              disabled={!form.countries}
              error={this.state.errors.origin_states}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            <MultiCitySelect
              label={isLocal ? "City *" : "Origin City *"}
              placeholder={`Search ${isLocal ? "City" : "Origin City"}`}
              selected={form.origin_cities || []}
              onChange={this.handleOriginCitiesChange}
              onInputChange={() =>
                this.setState((prev) => ({
                  errors: { ...prev.errors, origin_cities: "" },
                }))
              }
              countryCode={countryCode}
              stateName={form.origin_states}
              disabled={!form.origin_states}
              error={this.state.errors.origin_cities}
            />
          </Col>

          <Col lg={4}>
            <GeoLocationInput
              label="Origin Location"
              placeholder={
                originCitiesCount > 1
                  ? "Disabled for multiple cities"
                  : "Search Origin Location"
              }
              value={form.origin_location}
              onChange={this.setFormValue("origin_location")}
              disabled={isOriginLocationDisabled}
              countryCode={countryCode}
              stateName={form.origin_states}
              cityName={originCitiesCount === 1 ? form.origin_cities[0] : ""}
            />
          </Col>

          {!isLocal && (
            <Col lg={4}>
              <GooglePlaceInput
                label="Destination State *"
                placeholder="Search Destination State"
                value={form.dest_states}
                onChange={this.handleDestStateChange}
                searchType="state"
                countryCode={countryCode}
                disabled={!form.countries}
                error={this.state.errors.dest_states}
              />
            </Col>
          )}

          {!isLocal && (
            <Col lg={4}>
              <MultiCitySelect
                label="Destination City *"
                placeholder="Search Destination City"
                selected={form.dest_cities || []}
                onChange={this.handleDestCitiesChange}
                onInputChange={() =>
                  this.setState((prev) => ({
                    errors: { ...prev.errors, dest_cities: "" },
                  }))
                }
                countryCode={countryCode}
                stateName={form.dest_states}
                disabled={!form.dest_states}
                error={this.state.errors.dest_cities}
              />
            </Col>
          )}

          <Col lg={4}>
            <GeoLocationInput
              label="Destination Location"
              placeholder={
                (isLocal ? originCitiesCount > 1 : destCitiesCount > 1)
                  ? "Disabled for multiple cities"
                  : "Search Destination Location"
              }
              value={form.dest_location}
              onChange={this.setFormValue("dest_location")}
              disabled={isDestLocationDisabled}
              countryCode={countryCode}
              stateName={isLocal ? form.origin_states : form.dest_states}
              cityName={
                isLocal
                  ? originCitiesCount === 1
                    ? form.origin_cities[0]
                    : ""
                  : destCitiesCount === 1
                    ? form.dest_cities[0]
                    : ""
              }
            />
          </Col>
        </Row>

        {/* Distance Range */}
        <div className="pp-field-group-label">Distance Range</div>
        <Row>
          <Col lg={4}>
            {this.inp(
              `Initial Distance (${getDistanceUnit(form.currency, this.props.countries).toLocaleLowerCase()}) *`,
              "min_distance_km",
              "number",
              "Initial distance",
            )}
          </Col>
          <Col lg={4}>
            {this.inp(
              `Final Distance (${getDistanceUnit(form.currency, this.props.countries).toLocaleLowerCase()}) *`,
              "max_distance_km",
              "number",
              "Final distance",
            )}
          </Col>
          <Col lg={4}>
            {this.moneyInp(
              "Max Recommended Price *",
              "max_recommended_price",
              "Enter max recommended price",
            )}
          </Col>
        </Row>
        <div className="pp-field-group-label">Weight Range</div>
        <Row>
          <Col lg={4}>
            {this.inp(
              `Min Weight (${getWeightUnit(form.currency, this.props.countries).toLowerCase()}) *`,
              "min_weight_kg",
              "number",
              "Min weight",
            )}
          </Col>
          <Col lg={4}>
            {this.inp(
              `Max Weight (${getWeightUnit(form.currency, this.props.countries).toLowerCase()}) *`,
              "max_weight_kg",
              "number",
              "Max weight",
            )}
          </Col>
          <Col lg={4}>
            {this.inp(
              "Maximum Package Value",
              "max_package_value",
              "number",
              "Enter maximum package value",
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            <Form.Group className="custom-form">
              {this.renderLabel("Vehicle Types *")}
              <VehicleTypeSelect
                requestType={this.state.form.request_types}
                isInterstate={this.state.form.request_types === "INTERSTATE"}
                selected={this.state.form.vehicle_types || []}
                onChange={(next) =>
                  this.setState((prev) => ({
                    form: { ...prev.form, vehicle_types: next },
                    errors: { ...prev.errors, vehicle_types: "" },
                  }))
                }
              />
              {this.state.errors.vehicle_types && (
                <div className="text-danger small mt-1">
                  {this.state.errors.vehicle_types}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col lg={4}>
            {this.sel(
              "Delivery Mode",
              "delivery_modes",
              [
                { value: "DOOR_TO_DOOR", label: "Door to Door" },
                { value: "PARK_TO_PARK", label: "Park to Park" },
              ],
              "Select Delivery Mode",
              true,
            )}
          </Col>
        </Row>
      </>
    );
  }

  /* ── Step 2: Sender Pricing ── */
  renderStep2() {
    const { form, errors } = this.state;

    const senderAmountLabel =
      form.sender_pricing_mode === "FLAT"
        ? "Fixed Price *"
        : form.sender_pricing_mode === "DISCOUNT_PERCENT"
          ? "Percentage Discount (%) *"
          : form.sender_pricing_mode === "DISCOUNT_FIXED"
            ? "Fixed Discount Amount *"
            : form.sender_pricing_mode === "CAPPED"
              ? "Maximum Price Cap *"
              : "";

    const partnerAmountField =
      form.partner_pricing_mode === "FLAT"
        ? "partner_flat_amount"
        : form.partner_pricing_mode === "PERCENT_OF_SENDER"
          ? "partner_pct_of_sender"
          : null;

    const partnerAmountLabel =
      form.partner_pricing_mode === "FLAT"
        ? "Fixed Amount *"
        : form.partner_pricing_mode === "PERCENT_OF_SENDER"
          ? "% of Sender Price *"
          : "";

    return (
      <>
        {/* 1. Pricing Method */}
        <div className="pp-section-card mb-3">
          <p className="pp-section-title">Pricing Method</p>
          {this.radioGroup("sender_pricing_mode", SENDER_PRICING_MODES)}
          {errors.sender_pricing_mode && (
            <div className="text-danger small mt-1">
              {errors.sender_pricing_mode}
            </div>
          )}
          <hr className="mt-3 mb-3" />
          {senderAmountLabel && (
            <Row>
              <Col lg={4}>
                {form.sender_pricing_mode === "DISCOUNT_PERCENT"
                  ? this.inp(
                      senderAmountLabel,
                      "sender_discount_pct",
                      "number",
                      "0",
                    )
                  : form.sender_pricing_mode === "DISCOUNT_FIXED"
                    ? this.moneyInp(
                        senderAmountLabel,
                        "sender_discount_amt",
                        "0",
                      )
                    : form.sender_pricing_mode === "CAPPED"
                      ? this.moneyInp(
                          senderAmountLabel,
                          "sender_cap_amount",
                          "0",
                        )
                      : this.moneyInp(
                          senderAmountLabel,
                          "sender_flat_amount",
                          "0",
                        )}
              </Col>
              <Col lg={4}>
                {this.moneyInp("Floor Amount *", "sender_floor_amount", "0")}
              </Col>
            </Row>
          )}
        </div>

        {/* 2. Partner Pricing */}
        <div className="pp-section-card mb-3">
          <p className="pp-section-title">Partner Pricing</p>
          {this.radioGroup("partner_pricing_mode", PARTNER_PRICING_MODES)}
          {errors.partner_pricing_mode && (
            <div className="text-danger small mt-1">
              {errors.partner_pricing_mode}
            </div>
          )}
          <hr className="mt-3 mb-3" />
          {form.partner_pricing_mode !== "STANDARD" && partnerAmountField && (
            <Row>
              <Col lg={4}>
                {form.partner_pricing_mode === "PERCENT_OF_SENDER"
                  ? this.inp(
                      partnerAmountLabel,
                      "partner_pct_of_sender",
                      "number",
                      "0",
                    )
                  : this.moneyInp(partnerAmountLabel, partnerAmountField, "0")}
              </Col>
            </Row>
          )}
        </div>

        {/* 3. Usage Controls */}
        <div className="pp-section-card mb-3">
          <p className="pp-section-title">Usage Controls</p>
          <Row className="mt-3">
            <Col lg={3}>
              {this.moneyInp("Total Budget *", "total_budget", "0", null, 9)}
            </Col>
            <Col lg={3}>
              {this.sel(
                "Duration *",
                "duration",
                CONSTANTS.DURATION_OPTIONS,
                "Select Duration",
              )}
            </Col>
            <Col lg={3}>
              {this.inp(
                "Maximum Total Uses *",
                "total_usage_limit",
                "number",
                "0",
              )}
            </Col>
            <Col lg={3}>
              {this.inp(
                "Maximum Uses Per Sender *",
                "per_user_limit",
                "number",
                "0",
              )}
            </Col>
          </Row>
        </div>

        <div className="pp-section-card mb-3">
          <p className="pp-section-title">Display Settings</p>
          <Row className="mt-3">
            <Col lg={4}>
              <Form.Group className="custom-form">
                <Form.Label className="color-head">
                  Allow Promo Stacking
                </Form.Label>
                <div className="d-flex" style={{ gap: 12 }}>
                  <div className="checkboxContainer">
                    <input
                      type="radio"
                      id="stackable_with_promo_codes_yes"
                      name="stackable_with_promo_codes"
                      checked={form.stackable_with_promo_codes === "yes"}
                      onChange={() =>
                        this.setState((prev) => ({
                          form: {
                            ...prev.form,
                            stackable_with_promo_codes: "yes",
                          },
                        }))
                      }
                    />
                    <label
                      htmlFor="stackable_with_promo_codes_yes"
                      className="input-label"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="checkboxContainer">
                    <input
                      type="radio"
                      id="stackable_with_promo_codes_no"
                      name="stackable_with_promo_codes"
                      checked={form.stackable_with_promo_codes === "no"}
                      onChange={() =>
                        this.setState((prev) => ({
                          form: {
                            ...prev.form,
                            stackable_with_promo_codes: "no",
                          },
                        }))
                      }
                    />
                    <label
                      htmlFor="stackable_with_promo_codes_no"
                      className="input-label"
                    >
                      No
                    </label>
                  </div>
                </div>
              </Form.Group>
            </Col>
            <Col lg={4}>
              <Form.Group className="custom-form">
                <Form.Label className="color-head">Show Max Bid</Form.Label>
                <div className="d-flex" style={{ gap: 12 }}>
                  <div className="checkboxContainer">
                    <input
                      type="radio"
                      id="show_partner_bid_ceiling_yes"
                      name="show_partner_bid_ceiling"
                      checked={form.show_partner_bid_ceiling === "yes"}
                      onChange={() =>
                        this.setState((prev) => ({
                          form: {
                            ...prev.form,
                            show_partner_bid_ceiling: "yes",
                          },
                        }))
                      }
                    />
                    <label
                      htmlFor="show_partner_bid_ceiling_yes"
                      className="input-label"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="checkboxContainer">
                    <input
                      type="radio"
                      id="show_partner_bid_ceiling_no"
                      name="show_partner_bid_ceiling"
                      checked={form.show_partner_bid_ceiling === "no"}
                      onChange={() =>
                        this.setState((prev) => ({
                          form: {
                            ...prev.form,
                            show_partner_bid_ceiling: "no",
                          },
                        }))
                      }
                    />
                    <label
                      htmlFor="show_partner_bid_ceiling_no"
                      className="input-label"
                    >
                      No
                    </label>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <Form.Group className="custom-form">
                <Form.Label className="color-head">
                  Promotional Message
                </Form.Label>
                <Form.Control
                  type="text"
                  name="promo_message"
                  className="py-0"
                  value={form.promo_message || ""}
                  onChange={this.handleChange}
                  placeholder="Write promotional message"
                />
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="custom-form">
                <Form.Label className="color-head">Partner Message</Form.Label>
                <Form.Control
                  type="text"
                  name="partner_message"
                  className="py-0"
                  value={form.partner_message || ""}
                  onChange={this.handleChange}
                  placeholder="Write partner message"
                />
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="custom-form">
                <Form.Label className="color-head">
                  Terms &amp; Conditions Link
                </Form.Label>
                <Form.Control
                  type="url"
                  name="terms_url"
                  className="py-0"
                  value={form.terms_url || ""}
                  onChange={this.handleChange}
                  placeholder="Terms &amp; conditions link past here"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </>
    );
  }

  render() {
    const { loading, isEdit, isCopied, activeStep } = this.state;
    const isLastStep = activeStep === STEPS.length - 1;

    return (
      <div className="page__wrapper h-100">
        <Header {...this.props} />
        <div className="mainContainer CustomerTable">
          <Row noGutters className="h-100">
            <Sidebar {...this.props} />
            <Col md={9} lg={10} className="boxTyp3 d-flex flex-column">
              <div className="container-fluid flex-fill">
                <div className="content-wrapper">
                  <Loader showLoader={loading} />

                  <h3 className="font-weight-normal mb-1 line-ht color-33 fs-24">
                    {this.state.showPreview
                      ? "Promotion Preview"
                      : `${
                          isEdit && !isCopied
                            ? "Edit"
                            : isCopied
                              ? "Copy"
                              : "Create"
                        } Promotion`}
                  </h3>

                  {/* Step bar */}
                  {!this.state.showPreview && (
                    <div className="pp-step-bar mt-3">
                      {STEPS.map((step, idx) => {
                        const isFirst = idx === 0;
                        const isActive = activeStep === idx;
                        const isCompleted = idx < activeStep;
                        const cls = [
                          "pp-step",
                          "chain-tab",
                          isFirst ? "first-tab" : "",
                          isActive ? "active" : "",
                          isCompleted ? "completed" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <div
                            key={idx}
                            className={cls}
                            onClick={() =>
                              isCompleted &&
                              this.setState({ activeStep: idx, errors: {} })
                            }
                          >
                            {step}
                            {isCompleted ? (
                              <span className="pp-step-icon">
                                <img src={rightTick} alt="" />
                              </span>
                            ) : (
                              <span className="pp-step-icon">
                                <img src={infoCircle} alt="" />
                              </span>
                            )}
                          </div>
                        );
                      })}
                      <div className="pp-step-filler" />
                    </div>
                  )}

                  {/* Form card */}
                  <div
                    className={`${activeStep !== 2 ? "pp-form-card" : ""} mt-3`}
                  >
                    {this.state.showPreview ? (
                      this.renderPreviewPage()
                    ) : (
                      <>
                        {activeStep !== 2 && (
                          <p className="pp-card-title">{STEPS[activeStep]}</p>
                        )}

                        {activeStep === 0 && this.renderStep0()}
                        {activeStep === 1 && this.renderStep1()}
                        {activeStep === 2 && this.renderStep2()}
                      </>
                    )}
                  </div>

                  {/* Button bar */}
                  <div className="pp-btn-bar">
                    <button
                      type="button"
                      className="btnDefault btnOutline minw-150"
                      onClick={() =>
                        activeStep === 0
                          ? this.props.history.push("/promoPricingList")
                          : this.handleBack()
                      }
                    >
                      Cancel
                    </button>

                    <div className="d-flex">
                      {activeStep > 0 && (
                        <button
                          type="button"
                          className={`btnDefault btnOutline minw-150 ${
                            !isLastStep && "previous-btn"
                          }`}
                          onClick={this.handleBack}
                        >
                          Previous
                        </button>
                      )}

                      {!isLastStep && (
                        <button
                          type="button"
                          className={`btnDefault btnOutline minw-150 ${
                            activeStep > 0 && "next-btn"
                          }`}
                          onClick={this.handleNext}
                        >
                          Next
                        </button>
                      )}
                    </div>

                    {isLastStep && !this.state.showPreview && (
                      <button
                        type="button"
                        className="btnDefault btnOutline minw-150"
                        onClick={this.togglePreview}
                      >
                        Preview
                      </button>
                    )}

                    {!isEdit && (
                      <button
                        type="button"
                        className="btnDefault btnPrimary minw-150"
                        onClick={this.handleSaveDraft}
                        disabled={loading}
                      >
                        Save Draft
                      </button>
                    )}

                    {isLastStep && (
                      <button
                        type="button"
                        className="btnDefault btnPrimary minw-150"
                        onClick={this.handleSubmit}
                        disabled={loading}
                      >
                        {isCopied ? "Copy" : "Done"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <Footer />
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

export default connect(
  (state) => ({ countries: state.country.countries }),
  (dispatch) => ({ fetchCountries: () => dispatch(actionFetchCountries()) }),
)(CreateEditPromoPricing);
